(() => {
  const cartError = 'Unable to add this design. Please try again.';

  function optionValue(variant, name) {
    if (!variant || !variant.options) return undefined;
    if (!Array.isArray(variant.options)) return variant.options[name];
    const named = variant.options.find((option) => option && typeof option === 'object' && option.name === name);
    return named?.value;
  }

  function requiredOptionNamesAreValid(requiredOptionNames) {
    return Array.isArray(requiredOptionNames)
      && requiredOptionNames.length > 0
      && requiredOptionNames.every((name) => typeof name === 'string' && name.trim() !== '')
      && new Set(requiredOptionNames).size === requiredOptionNames.length;
  }

  function variantHasCompleteOptions(variant, requiredOptionNames) {
    if (!variant || variant.id === undefined || variant.id === null || String(variant.id).trim() === '') return false;
    if (Array.isArray(variant.options)) {
      if (variant.options.length !== requiredOptionNames.length) return false;
      return requiredOptionNames.every((name) => {
        const matches = variant.options.filter((option) => option && typeof option === 'object' && option.name === name);
        return matches.length === 1 && typeof matches[0].value === 'string' && matches[0].value.trim() !== '';
      });
    }
    if (!variant.options || typeof variant.options !== 'object') return false;
    const keys = Object.keys(variant.options);
    return keys.length === requiredOptionNames.length
      && requiredOptionNames.every((name) => Object.prototype.hasOwnProperty.call(variant.options, name)
        && typeof variant.options[name] === 'string' && variant.options[name].trim() !== '');
  }

  function hasUsableMadeToOrderVariant(variants, requiredOptionNames) {
    return requiredOptionNamesAreValid(requiredOptionNames)
      && Array.isArray(variants)
      && variants.some((variant) => variant?.available === true && variantHasCompleteOptions(variant, requiredOptionNames));
  }

  function resolveMadeToOrderVariant(variants, selections = {}, requiredOptionNames = []) {
    if (!hasUsableMadeToOrderVariant(variants, requiredOptionNames)
      || !requiredOptionNames.every((name) => selections[name])) return null;
    const matches = variants.filter((variant) => variant?.available === true
      && variantHasCompleteOptions(variant, requiredOptionNames)
      && requiredOptionNames.every((name) => optionValue(variant, name) === selections[name]));
    return matches.length === 1 ? matches[0] : null;
  }

  function activeDollRecord(state = {}) {
    if (!state.requiresDoll || typeof state.doll !== 'string' || state.doll.trim() === '') return null;
    const matches = (Array.isArray(state.dolls) ? state.dolls : [])
      .filter((record) => record?.available === true && record.name === state.doll);
    return matches.length === 1 ? matches[0] : null;
  }

  function validateMadeToOrderState(state = {}) {
    const names = Array.isArray(state.requiredOptionNames) ? state.requiredOptionNames : [];
    const selections = state.selections || {};
    if (state.reservationStatus !== 'available') {
      return { ok: false, field: 'reservation', message: 'This design is fully booked.' };
    }
    if (!hasUsableMadeToOrderVariant(state.variants, names)) {
      return { ok: false, field: 'product', message: 'This design is currently unavailable.' };
    }
    const missingName = names.find((name) => !selections[name]);
    if (missingName) return { ok: false, field: missingName, message: `Choose ${missingName} to continue.` };
    if (state.requiresDoll && !activeDollRecord(state)) {
      return { ok: false, field: 'doll', message: 'Choose your Doll to continue.' };
    }
    if (!resolveMadeToOrderVariant(state.variants, selections, names)) {
      return { ok: false, field: names.at(-1) || 'options', message: 'This combination is unavailable.' };
    }
    return { ok: true };
  }

  function optionChoiceIsReachable(variants, selections, requiredOptionNames, optionName, optionValueCandidate) {
    const optionIndex = requiredOptionNames.indexOf(optionName);
    if (!Array.isArray(variants) || optionIndex < 0) return false;
    const earlierNames = requiredOptionNames.slice(0, optionIndex);
    return variants.some((variant) => variant?.available === true
      && variantHasCompleteOptions(variant, requiredOptionNames)
      && optionValue(variant, optionName) === optionValueCandidate
      && earlierNames.every((name) => !selections[name] || optionValue(variant, name) === selections[name]));
  }

  function setBooleanAttribute(node, name, value) {
    if (!node) return;
    if (value) node.setAttribute(name, 'true');
    else node.removeAttribute(name);
  }

  function optionChoices(root) {
    return [...(root?.querySelectorAll?.('[data-kosbling-toc-option]') || [])];
  }

  function optionField(root, name) {
    return [...(root?.querySelectorAll?.('[data-kosbling-option-name]') || [])]
      .find((field) => field.dataset?.kosblingOptionName === name);
  }

  function renderMadeToOrder(root, state) {
    if (!root || !state) return state;
    const names = Array.isArray(state.requiredOptionNames) ? state.requiredOptionNames : [];
    const selections = state.selections || {};
    if (state.requiresDoll && state.doll && !activeDollRecord(state)) state.doll = '';

    optionChoices(root).forEach((choice) => {
      const name = choice.dataset?.kosblingOptionName;
      choice.checked = selections[name] === choice.value;
      choice.disabled = !optionChoiceIsReachable(state.variants, selections, names, name, choice.value);
      setBooleanAttribute(choice, 'aria-disabled', choice.disabled);
    });
    root.querySelectorAll?.('[data-kosbling-toc-choice="doll"]').forEach((choice) => {
      const record = activeDollRecord({ ...state, doll: choice.value });
      choice.disabled = !record;
      choice.checked = state.doll === choice.value;
      setBooleanAttribute(choice, 'aria-disabled', choice.disabled);
    });
    root.querySelectorAll?.('[data-kosbling-toc-media]').forEach((media, index) => {
      setBooleanAttribute(media, 'aria-current', index === Number(state.mediaIndex || 0));
    });

    const variant = resolveMadeToOrderVariant(state.variants, selections, names);
    const selectedEntries = Object.entries(selections).filter(([, value]) => Boolean(value));
    const builderUsable = hasUsableMadeToOrderVariant(state.variants, names);
    const priceVariant = variant || (state.variants || []).find((candidate) => candidate?.available === true
      && variantHasCompleteOptions(candidate, names)
      && selectedEntries.every(([name, value]) => optionValue(candidate, name) === value));
    const variantInput = root.querySelector?.('[data-kosbling-toc-variant-id]');
    const dollInput = root.querySelector?.('[data-kosbling-toc-property="doll"]');
    const price = root.querySelector?.('[data-kosbling-toc-price]');
    const status = root.querySelector?.('[data-kosbling-toc-status]');
    const summary = root.querySelector?.('[data-kosbling-toc-summary]');
    const submit = root.querySelector?.('[data-kosbling-toc-submit]');
    const productImage = root.querySelector?.('[data-kosbling-toc-product-image]');
    if (variantInput) variantInput.value = variant ? String(variant.id) : '';
    if (dollInput) dollInput.value = state.requiresDoll ? state.doll || '' : '';
    if (price) price.textContent = priceVariant ? `$${priceVariant.price}` : 'Choose options';
    if (status) {
      status.textContent = state.reservationStatus !== 'available'
        ? 'Fully booked'
        : builderUsable ? 'Available' : 'Sold out';
    }
    if (productImage && variant?.featuredMediaUrl) {
      productImage.src = variant.featuredMediaUrl;
      productImage.alt = variant.featuredMediaAlt || productImage.alt;
      const width = Number(variant.featuredMediaWidth);
      const height = Number(variant.featuredMediaHeight);
      if (Number.isFinite(width) && width > 0) {
        productImage.width = width;
        productImage.setAttribute('width', String(width));
      }
      if (Number.isFinite(height) && height > 0) {
        productImage.height = height;
        productImage.setAttribute('height', String(height));
      }
    }
    if (summary) {
      const parts = names.map((name) => selections[name]).filter(Boolean);
      if (state.requiresDoll && state.doll) parts.push(state.doll);
      summary.textContent = parts.join(' · ') || 'Choose your options';
    }
    if (submit) setBooleanAttribute(submit, 'aria-disabled', !validateMadeToOrderState(state).ok);
    return state;
  }

  function focusInvalid(root, result) {
    const field = result.field === 'doll'
      ? root.querySelector?.('[data-kosbling-toc-field="doll"]')
      : optionField(root, result.field);
    (field || root.querySelector?.('[data-kosbling-toc-submit]'))?.focus?.();
  }

  async function submitMadeToOrder(root, event) {
    event?.preventDefault?.();
    const state = root?.kosblingMadeToOrderState || root?.kosblingTocState;
    const submit = root?.querySelector?.('[data-kosbling-toc-submit]');
    if (!state || state.submitting) return false;
    const result = validateMadeToOrderState(state);
    const error = root?.querySelector?.('[data-kosbling-toc-error]');
    if (!result.ok) {
      if (error) error.textContent = result.message;
      focusInvalid(root, result);
      return false;
    }

    const selectedVariant = resolveMadeToOrderVariant(state.variants, state.selections, state.requiredOptionNames);
    const properties = { 'Made to order': 'Ships in approximately 12–15 days' };
    if (state.requiresDoll) properties.Doll = state.doll;
    const view = root.ownerDocument?.defaultView || window;
    state.submitting = true;
    if (submit) {
      submit.disabled = true;
      setBooleanAttribute(submit, 'aria-busy', true);
    }
    try {
      const response = await view.fetch(view.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(selectedVariant.id), quantity: 1, properties })
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok || responseData.status || responseData.errors) throw new Error('Shopify cart add failed');
      view.location.assign(view.routes.cart_url || '/cart');
      return true;
    } catch {
      if (error) error.textContent = cartError;
      return false;
    } finally {
      state.submitting = false;
      if (submit) {
        submit.disabled = false;
        setBooleanAttribute(submit, 'aria-busy', false);
      }
    }
  }

  function initMadeToOrder(root) {
    if (!root || root.dataset.kosblingTocReady === 'true') return root?.kosblingMadeToOrderState;
    const dataNode = root.querySelector?.('[data-kosbling-toc-data]');
    let data = {};
    try { data = JSON.parse(dataNode?.textContent || '{}'); } catch { data = {}; }
    const requiredOptionNames = Array.isArray(data.requiredOptionNames) ? data.requiredOptionNames : [];
    const state = {
      variants: Array.isArray(data.variants) ? data.variants : [],
      requiredOptionNames,
      selections: Object.fromEntries(requiredOptionNames.map((name) => [name, ''])),
      requiresDoll: data.requiresDoll === true,
      dolls: Array.isArray(data.dolls) ? data.dolls : [],
      reservationStatus: data.reservationStatus === 'available' ? 'available' : 'fully_booked',
      doll: '',
      mediaIndex: 0,
      submitting: false
    };
    root.dataset.kosblingTocReady = 'true';
    root.kosblingMadeToOrderState = state;
    root.kosblingTocState = state;

    optionChoices(root).forEach((choice) => {
      choice.addEventListener('change', () => {
        if (choice.disabled) return;
        const optionName = choice.dataset.kosblingOptionName;
        const optionIndex = state.requiredOptionNames.indexOf(optionName);
        if (state.selections[optionName] !== choice.value) {
          state.requiredOptionNames.slice(optionIndex + 1).forEach((name) => { state.selections[name] = ''; });
        }
        state.selections[optionName] = choice.value;
        const error = root.querySelector?.('[data-kosbling-toc-error]');
        if (error) error.textContent = '';
        renderMadeToOrder(root, state);
      });
    });
    if (state.requiresDoll) {
      root.querySelectorAll?.('[data-kosbling-toc-choice="doll"]').forEach((choice) => {
        choice.addEventListener('change', () => {
          if (choice.disabled) return;
          state.doll = choice.value;
          const error = root.querySelector?.('[data-kosbling-toc-error]');
          if (error) error.textContent = '';
          renderMadeToOrder(root, state);
        });
      });
    }
    root.querySelector?.('[data-kosbling-toc-form]')?.addEventListener('submit', (event) => submitMadeToOrder(root, event));
    renderMadeToOrder(root, state);
    return state;
  }

  function legacyOptionNames(variants) {
    const options = variants?.[0]?.options;
    return Array.isArray(options) ? options.map((option) => option?.name).filter(Boolean) : Object.keys(options || {});
  }

  function resolveTocVariant(variants, firstValue, secondValue) {
    const names = legacyOptionNames(variants).slice(0, 2);
    return resolveMadeToOrderVariant(variants, { [names[0]]: firstValue, [names[1]]: secondValue }, names);
  }

  function legacyState(state = {}) {
    const names = legacyOptionNames(state.variants).slice(0, 2);
    return {
      ...state,
      requiredOptionNames: names,
      selections: { [names[0]]: state.capacity || '', [names[1]]: state.color || '' },
      requiresDoll: true
    };
  }

  function validateTocState(state = {}) {
    const normalized = legacyState(state);
    const result = validateMadeToOrderState(normalized);
    if (!result.ok && result.field === normalized.requiredOptionNames[0]) {
      return { ...result, field: 'capacity', message: `Choose a ${result.field.toLowerCase()} to continue.` };
    }
    if (!result.ok && result.field === normalized.requiredOptionNames[1]) {
      return { ...result, field: 'color', message: `Choose a ${result.field} to continue.` };
    }
    return result;
  }

  function renderToc(root, state) {
    const normalized = legacyState(state);
    normalized.doll = state.doll || '';
    const originalQueryAll = root.querySelectorAll.bind(root);
    const firstChoices = originalQueryAll('[data-kosbling-toc-choice="capacity"]');
    const secondChoices = originalQueryAll('[data-kosbling-toc-choice="color"]');
    [...firstChoices].forEach((choice) => { choice.dataset.kosblingOptionName = normalized.requiredOptionNames[0]; });
    [...secondChoices].forEach((choice) => { choice.dataset.kosblingOptionName = normalized.requiredOptionNames[1]; });
    const generic = [...firstChoices, ...secondChoices];
    root.querySelectorAll = (selector) => selector === '[data-kosbling-toc-option]' ? generic : originalQueryAll(selector);
    renderMadeToOrder(root, normalized);
    root.querySelectorAll = originalQueryAll;
    state.doll = normalized.doll;
    return state;
  }

  function submitToc(root, event) {
    if (root?.kosblingTocState && !root.kosblingMadeToOrderState) root.kosblingMadeToOrderState = legacyState(root.kosblingTocState);
    return submitMadeToOrder(root, event);
  }

  function initToc(root) {
    return initMadeToOrder(root);
  }

  window.KosblingToc = Object.freeze({
    hasUsableMadeToOrderVariant, resolveMadeToOrderVariant, activeDollRecord, validateMadeToOrderState, renderMadeToOrder, submitMadeToOrder, initMadeToOrder,
    resolveTocVariant, validateTocState, renderToc, submitToc, initToc
  });
  document.querySelectorAll('[data-kosbling-toc]').forEach((root) => initMadeToOrder(root));
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target?.matches?.('[data-kosbling-toc]')) initMadeToOrder(target);
    target?.querySelectorAll?.('[data-kosbling-toc]').forEach((root) => initMadeToOrder(root));
  });
})();
